class ProductSingle extends HTMLElement {
  constructor() {
    super();
    this.variantGalleryMap = null;
    this.originalSlides = [];
    this.originalThumbnailsHTML = '';
    this.isCustomGallery = false;
  }

  connectedCallback() {
    this.slider = this.querySelector('.js-product-slider');
    this.thumbnailContainer = this.querySelector('.js-product-slider-nav-thumbs');

    // Save original gallery HTML BEFORE Slick modifies the DOM
    this.saveOriginalGallery();

    this.loadVariantGalleryMap();

    // Apply variant gallery for the initially selected variant once Slick initializes
    this.applyInitialVariantGallery();

    // Listen for variant changes. The theme's <variant-selects> / <variant-radios>
    // dispatches `variants:change` WITHOUT bubbles, so a document-level listener
    // never fires — attach to the dispatching element directly. Belt-and-braces
    // with the `variant =` setter call from theme.js (updateVariantInput).
    this.variantSelector = this.querySelector('variant-selects, variant-radios');
    this._boundHandleVariantChange = this.handleVariantChange.bind(this);
    if (this.variantSelector) {
      this.variantSelector.addEventListener('variants:change', this._boundHandleVariantChange);
    }
  }

  disconnectedCallback() {
    if (this.variantSelector && this._boundHandleVariantChange) {
      this.variantSelector.removeEventListener('variants:change', this._boundHandleVariantChange);
    }
  }

  loadVariantGalleryMap() {
    const mapScript = document.querySelector('[data-variant-gallery-map]');
    if (mapScript) {
      try {
        this.variantGalleryMap = JSON.parse(mapScript.textContent);
      } catch (e) {
        console.error('Failed to parse variant gallery map:', e);
      }
    }
  }

  applyInitialVariantGallery() {
    const initialVariantId = this.dataset.initialVariantId;
    if (!initialVariantId || !this.variantGalleryMap) return;

    const galleryImages = this.variantGalleryMap[initialVariantId];
    if (!galleryImages || galleryImages.length < 1) return;

    const apply = () => {
      // If a user-driven variant change already happened with a different
      // variant, don't clobber their selection. (Without this guard the late
      // observer wake-up during the first variant switch — when Slick is
      // unslicked + re-initialized — would override the just-picked variant
      // with the initial variant's gallery.)
      if (this._variant && this._variant.id !== parseInt(initialVariantId)) return;
      this._variant = { id: parseInt(initialVariantId) };
      this.replaceGalleryWithMetafieldImages(galleryImages);
    };

    // If Slick is already initialized by the time this custom element
    // connects (depends on script load order), the MutationObserver below
    // will never fire — there's no class change left to observe. Apply
    // immediately on the next macrotask instead.
    if (this.slider.classList.contains('slick-initialized')) {
      setTimeout(apply, 0);
      return;
    }

    // Slick hasn't initialized yet — watch for it.
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class' &&
          this.slider.classList.contains('slick-initialized')
        ) {
          observer.disconnect();
          // Delay to next macrotask so the theme's own runAfterSlickInit
          // MutationObserver completes its post-init setup first.
          // Without this, both observers race on the same mutation,
          // causing duplicate handlers and broken gallery state.
          setTimeout(apply, 0);
          return;
        }
      }
    });

    observer.observe(this.slider, { attributes: true, attributeFilter: ['class'] });
  }

  saveOriginalGallery() {
    if (!this.slider) return;

    // Save clean slide HTML before Slick adds its classes/styles
    this.originalSlides = Array.from(
      this.slider.querySelectorAll('.media-gallery__wrapper')
    ).map(slide => slide.outerHTML);

    if (this.thumbnailContainer) {
      const thumbnailList = this.thumbnailContainer.querySelector('.thumbnail-list');
      if (thumbnailList) {
        this.originalThumbnailsHTML = thumbnailList.innerHTML;
      }
    }
  }

  handleVariantChange(event) {
    if (event.detail && event.detail.variant) {
      this._variant = event.detail.variant;
      this.updateMedia();
    }
  }

  updateMedia() {
    if (!this.slider || !this._variant || typeof $ !== 'function') return;
    if (!$(this.slider).hasClass('slick-initialized')) return;

    const variantId = this._variant.id.toString();
    const galleryImages = this.variantGalleryMap && this.variantGalleryMap[variantId];

    if (galleryImages && galleryImages.length >= 1) {
      this.replaceGalleryWithMetafieldImages(galleryImages);
    } else {
      this.restoreOriginalGallery();
    }
  }

  replaceGalleryWithMetafieldImages(images) {
    const $slider = $(this.slider);
    if (!$slider.hasClass('slick-initialized')) return;

    // Destroy Slick completely so we can rebuild the DOM cleanly
    $slider.slick('unslick');

    // Remove all existing slides
    this.slider.querySelectorAll('.media-gallery__wrapper').forEach(s => s.remove());

    // Insert fresh slide HTML (no stale srcset attributes)
    images.forEach((image, index) => {
      this.slider.insertAdjacentHTML('beforeend', this.createSlideHTML(image, index));
    });

    // Rebuild thumbnail strip
    this.updateThumbnails(images);

    // Start at first slide for variant gallery
    // Must use jQuery .data() instead of native dataset to update jQuery's internal cache,
    // which thumbsCarouselInit reads via $sliderElement.data('slideId')
    $(this.slider).data('slideId', 0);

    // Clean up stale beforeChange/afterChange handlers from previous initiateSlick
    // calls — unslick does NOT remove them (they aren't .slick-namespaced)
    $(this.slider).off('beforeChange afterChange');

    // Reinitialize Slick + thumbnail click handlers + active states via theme
    if (typeof theme !== 'undefined' && theme.thumbsCarouselInit) {
      theme.thumbsCarouselInit(this.slider);
    }

    this.ensureSlideAnimation();

    this.isCustomGallery = true;
  }

  restoreOriginalGallery() {
    const $slider = $(this.slider);

    if (!this.isCustomGallery) {
      // Already showing original gallery, just navigate to featured media
      if (this._variant.featured_media) {
        this.navigateToMediaId(this._variant.featured_media.id.toString());
      }
      return;
    }

    if (!$slider.hasClass('slick-initialized')) return;

    // Destroy Slick
    $slider.slick('unslick');

    // Remove current variant slides
    this.slider.querySelectorAll('.media-gallery__wrapper').forEach(s => s.remove());

    // Restore original slide HTML
    this.originalSlides.forEach(html => {
      this.slider.insertAdjacentHTML('beforeend', html);
    });

    // Restore original thumbnails
    if (this.thumbnailContainer && this.originalThumbnailsHTML) {
      const thumbnailList = this.thumbnailContainer.querySelector('.thumbnail-list');
      if (thumbnailList) {
        thumbnailList.innerHTML = this.originalThumbnailsHTML;
      }
    }

    // Navigate to featured media on reinit
    if (this._variant.featured_media) {
      const mediaId = this._variant.featured_media.id.toString();
      const slideIndex = this.findSlideIndexForMedia(mediaId);
      $(this.slider).data('slideId', slideIndex);
    } else {
      $(this.slider).data('slideId', 0);
    }

    // Clean up stale beforeChange/afterChange handlers from previous initiateSlick
    // calls — unslick does NOT remove them (they aren't .slick-namespaced)
    $(this.slider).off('beforeChange afterChange');

    // Reinitialize Slick + thumbnail click handlers + active states via theme
    if (typeof theme !== 'undefined' && theme.thumbsCarouselInit) {
      theme.thumbsCarouselInit(this.slider);
    }

    this.ensureSlideAnimation();

    this.isCustomGallery = false;
  }

  findSlideIndexForMedia(mediaId) {
    const items = this.slider.querySelectorAll('.media-gallery__item');
    for (let i = 0; i < items.length; i++) {
      if (items[i].dataset.mediaId === mediaId) return i;
    }
    return 0;
  }

  getImageRatioConfig() {
    const ratio = this.dataset.imageRatio || 'natural';
    if (ratio === 'natural') {
      return { cssClass: 'natural', useInlineStyle: true };
    }
    // Fixed ratio like "1:1", "3:4", "4:3", "16:9", "2:3"
    return { cssClass: ratio, useInlineStyle: false };
  }

  createSlideHTML(image, index) {
    const ratioConfig = this.getImageRatioConfig();
    const naturalAspectRatio = image.height > 0 ? (image.height / image.width) * 100 : 100;
    const ratioClass = `o-ratio--${ratioConfig.cssClass}`;
    const inlineStyle = ratioConfig.useInlineStyle ? `style="padding-bottom: ${naturalAspectRatio}%;"` : '';

    return `
      <div class="media-gallery__wrapper" data-slide-id="${index}" notab="notab">
        <div class="media-gallery__item media-gallery__item--image" data-slide-id="${index}" data-media-id="custom-${index}">
          <div class="o-ratio ${ratioClass}" ${inlineStyle}>
            <div class="o-ratio__content">
              <a
                class="media-gallery__link"
                href="${image.url}"
                data-pswp-width="${image.width || 1000}"
                data-pswp-height="${image.height || 1000}"
                data-slide-id="${index}"
              >
                <img
                  class="media-gallery__image"
                  src="${image.url}"
                  alt="${image.alt || ''}"
                  width="${image.width || 1000}"
                  height="${image.height || 1000}"
                  loading="${index === 0 ? 'eager' : 'lazy'}"
                  data-media-id="custom-${index}"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  updateThumbnails(images) {
    if (!this.thumbnailContainer) return;
    const thumbnailList = this.thumbnailContainer.querySelector('.thumbnail-list');
    if (!thumbnailList) return;

    thumbnailList.innerHTML = '';

    images.forEach((image, index) => {
      const li = document.createElement('li');
      li.className = 'thumbnail-list__item';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'thumbnail';
      button.setAttribute('aria-label', `Thumbnail ${index + 1}`);
      button.setAttribute('data-slide-number', index.toString());

      const img = document.createElement('img');
      img.src = image.thumb;
      img.alt = image.alt || '';
      img.className = 'media-gallery__thumb-image';
      img.loading = 'lazy';
      img.setAttribute('data-media-id', `custom-${index}`);
      // Also set data-slide-number on img so click handler works regardless of target
      img.setAttribute('data-slide-number', index.toString());

      button.appendChild(img);
      li.appendChild(button);
      thumbnailList.appendChild(li);
    });
  }

  ensureSlideAnimation() {
    // After thumbsCarouselInit, Slick is initialized synchronously but the
    // MutationObserver that sets speed/cssEase fires as a microtask.
    // Use requestAnimationFrame to run after both, guaranteeing the slider
    // has proper animation settings for thumbnail click transitions.
    requestAnimationFrame(() => {
      const $slider = $(this.slider);
      if (!$slider.hasClass('slick-initialized')) return;
      $slider.slick('slickSetOption', 'speed', 800);
      $slider.slick('slickSetOption', 'cssEase', 'cubic-bezier(0.25, 0.46, 0.45, 0.94)');
    });
  }

  navigateToMediaId(mediaId) {
    if (typeof $ !== 'function' || !$(this.slider).hasClass('slick-initialized')) return;
    const slide = this.querySelector(`.media-gallery__item[data-media-id="${mediaId}"]`);
    if (!slide) return;
    const slideId = slide.closest('.media-gallery__wrapper')?.dataset.slideId || slide.dataset.slideId;
    if (slideId !== undefined && slideId !== null) {
      $(this.slider).slick('slickGoTo', parseInt(slideId));
    }
  }

  set variant(variant) {
    this._variant = variant;
    this.updateMedia();
  }

  get variant() {
    return this._variant;
  }
}
customElements.define('product-single', ProductSingle);
